import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FilePlus2,
  FolderSearch,
  FileOutput,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  HelpCircle,
  Globe2,
} from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import NotificationBell from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { pingPageView } from "@/lib/admin-api";

// Lightweight session id stored in sessionStorage (per browser tab).
function getSessionId() {
  try {
    let sid = sessionStorage.getItem("vp_analytics_sid");
    if (!sid) {
      sid = `vp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem("vp_analytics_sid", sid);
    }
    return sid;
  } catch {
    return null;
  }
}

const NAV_ITEMS = [
  { to: "/app/dashboard", label: "Tableau de bord", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/app/new-project", label: "Nouveau projet", icon: FilePlus2, testId: "nav-new-project" },
  { to: "/app/analyses", label: "Mes analyses", icon: FolderSearch, testId: "nav-analyses" },
  { to: "/app/data-collector", label: "Collecteur de Données", icon: Globe2, testId: "nav-data-collector" },
  { to: "/app/reports", label: "Rapports", icon: FileOutput, testId: "nav-reports" },
  { to: "/app/settings", label: "Paramètres", icon: Settings, testId: "nav-settings" },
];

function initialsOf(name, email) {
  if (name) {
    const parts = name.trim().split(/\s+/);
    return (parts[0]?.[0] || "") + (parts[1]?.[0] || parts[0]?.[1] || "");
  }
  return (email?.[0] || "U").toUpperCase();
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Analytics — track page-view + emit duration on unmount/route change.
  const pageEnterRef = useRef(Date.now());
  const lastPathRef = useRef(null);
  useEffect(() => {
    const path = location.pathname;
    if (lastPathRef.current === path) return;
    // Emit duration for previous page
    if (lastPathRef.current) {
      const elapsed = Math.round((Date.now() - pageEnterRef.current) / 1000);
      pingPageView({
        page_path: lastPathRef.current,
        session_id: getSessionId(),
        duration_seconds: elapsed > 0 ? elapsed : 1,
        referrer: typeof document !== "undefined" ? document.referrer : null,
      });
    }
    // Mark entry for the new page (sent on next route change / unmount).
    pageEnterRef.current = Date.now();
    lastPathRef.current = path;
    // Also fire an immediate ping for live visit counting.
    pingPageView({
      page_path: path,
      session_id: getSessionId(),
      duration_seconds: 0,
      referrer: typeof document !== "undefined" ? document.referrer : null,
    });
  }, [location.pathname]);

  // Flush remaining duration when the tab is closed.
  useEffect(() => {
    const flush = () => {
      if (!lastPathRef.current) return;
      const elapsed = Math.round((Date.now() - pageEnterRef.current) / 1000);
      pingPageView({
        page_path: lastPathRef.current,
        session_id: getSessionId(),
        duration_seconds: elapsed > 0 ? elapsed : 1,
      });
    };
    window.addEventListener("beforeunload", flush);
    return () => window.removeEventListener("beforeunload", flush);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // Global F1 shortcut → open the Help & Tutorial Center.
  // Ignored when the user is typing in an editable field so we don't steal
  // browser defaults inside inputs/textareas/contenteditable surfaces.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "F1") return;
      const t = e.target;
      const tag = t?.tagName;
      if (
        t?.isContentEditable ||
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT"
      ) {
        return;
      }
      e.preventDefault();
      navigate("/tutorial");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  const SidebarInner = (
    <div className="flex h-full w-full flex-col">
      {/* Brand */}
      <div className="flex items-center gap-3 px-5 pt-5 pb-4">
        <Logo size={38} />
        <div className="min-w-0">
          <div className="text-base font-bold leading-none tracking-tight">
            VITAE-PUBLICA<span className="text-[0.55em] align-top ml-0.5">™</span>
          </div>
          <div className="mt-1 text-[11px] italic text-muted-foreground truncate">
            Les Finances Publiques sont des Tranches de Vies
          </div>
        </div>
      </div>

      <Separator />

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 scrollbar-sleek">
        <ul className="space-y-1" data-testid="sidebar-nav">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  data-testid={item.testId}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "group flex items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-foreground/80 hover:bg-accent/60 hover:text-foreground",
                    )
                  }
                >
                  <span className="flex items-center gap-3">
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </span>
                  <ChevronRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-60 transition-opacity" />
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer / disclaimer */}
      <div className="p-3">
        <div
          className="rounded-md border border-[hsl(var(--brand-gold))/30] bg-accent/30 p-3 text-[11px] leading-snug text-muted-foreground"
          data-testid="sidebar-disclaimer"
        >
          <div className="font-semibold text-foreground mb-1">Méthodologie vitaliste</div>
          Unité de compte : la <strong className="text-brand-blue dark:text-brand-gold">vie humaine</strong>.
          Formule&nbsp;: J = P / (S/30).
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="app-shell">
      {/* Header */}
      <header
        className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-4 backdrop-blur-md md:pl-72"
        data-testid="app-header"
      >
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Ouvrir la navigation"
            data-testid="sidebar-toggle-btn"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <div className="md:hidden flex items-center gap-2">
            <Logo size={26} />
            <span className="text-sm font-semibold">VITAE-PUBLICA™</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <NotificationBell />
          <ThemeToggle />

          {/* Help & Tutorial Center trigger — visible on every authed page */}
          <button
            type="button"
            onClick={() => navigate("/tutorial")}
            aria-label="Ouvrir l'aide et le tutoriel (F1)"
            aria-keyshortcuts="F1"
            title="Aide & tutoriel (F1)"
            data-testid="htc-trigger"
            className="group flex h-11 w-11 flex-col items-center justify-center rounded-full text-brand-gold transition-all hover:bg-brand-gold/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
          >
            <span
              className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-brand-gold bg-brand-gold/10 transition-colors group-hover:bg-brand-gold group-hover:text-white group-active:brightness-90"
            >
              <HelpCircle className="h-4 w-4" />
            </span>
            <span className="mt-0.5 text-[9px] font-semibold uppercase leading-none tracking-wider text-brand-gold">
              Aide
            </span>
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="user-menu-trigger"
                className="flex items-center gap-2 rounded-full border border-border bg-card px-2 py-1 text-sm transition-colors hover:bg-accent/60"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                  {initialsOf(user?.name, user?.email)}
                </span>
                <span className="hidden max-w-[180px] truncate sm:inline">
                  {user?.name || user?.email}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="truncate">
                {user?.email}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="menu-settings"
                onClick={() => navigate("/app/settings")}
              >
                <Settings className="mr-2 h-4 w-4" /> Paramètres
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="menu-logout"
                onClick={handleLogout}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" /> Se déconnecter
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Sidebar (desktop fixed) */}
      <aside
        className="fixed inset-y-0 left-0 z-40 hidden w-72 border-r border-border bg-card md:block"
        data-testid="sidebar-desktop"
      >
        {SidebarInner}
      </aside>

      {/* Sidebar (mobile overlay) */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 md:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <aside
            className="fixed inset-y-0 left-0 z-50 w-72 border-r border-border bg-card md:hidden"
            data-testid="sidebar-mobile"
          >
            {SidebarInner}
          </aside>
        </>
      )}

      {/* Main content */}
      <main className="md:pl-72" data-testid="app-main">
        <div className="min-h-[calc(100vh-56px)] p-5 md:p-8">{children}</div>
      </main>
    </div>
  );
}
